import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { API } from '../App';
import { Shield, LogOut, Users, FileCheck, DollarSign, TrendingUp, UserPlus, Briefcase, MapPin, Star, Award } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Button } from '../components/ui/button';

function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Field Agent states
  const [agents, setAgents] = useState([]);
  const [agentJobs, setAgentJobs] = useState([]);
  const [showAddAgentDialog, setShowAddAgentDialog] = useState(false);
  const [agentForm, setAgentForm] = useState({
    full_name: '',
    phone: '',
    email: '',
    regions_covered: []
  });
  const [regionInput, setRegionInput] = useState('');

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user'));
    if (userData.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    setUser(userData);
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      await Promise.all([
        fetchStats(),
        fetchAgents(),
        fetchAgentJobs()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`);
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load statistics');
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${API}/agents`);
      setAgents(response.data);
    } catch (error) {
      console.error('Failed to load agents:', error);
    }
  };

  const fetchAgentJobs = async () => {
    try {
      const response = await axios.get(`${API}/agents/jobs`);
      setAgentJobs(response.data);
    } catch (error) {
      console.error('Failed to load agent jobs:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
    toast.success('Logged out successfully');
  };

  const handleAddRegion = () => {
    if (regionInput.trim() && !agentForm.regions_covered.includes(regionInput.trim())) {
      setAgentForm({
        ...agentForm,
        regions_covered: [...agentForm.regions_covered, regionInput.trim()]
      });
      setRegionInput('');
    }
  };

  const handleRemoveRegion = (region) => {
    setAgentForm({
      ...agentForm,
      regions_covered: agentForm.regions_covered.filter(r => r !== region)
    });
  };

  const handleCreateAgent = async (e) => {
    e.preventDefault();
    
    if (agentForm.regions_covered.length === 0) {
      toast.error('Please add at least one region');
      return;
    }

    try {
      const response = await axios.post(`${API}/agents/register`, agentForm);
      toast.success(`Agent registered successfully! Code: ${response.data.agent_code}`);
      setShowAddAgentDialog(false);
      setAgentForm({
        full_name: '',
        phone: '',
        email: '',
        regions_covered: []
      });
      fetchAgents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to register agent');
    }
  };

  const handleAssignJob = async (jobId, agentId) => {
    try {
      await axios.post(`${API}/agents/jobs/${jobId}/assign/${agentId}`);
      toast.success('Job assigned successfully!');
      fetchAgentJobs();
    } catch (error) {
      toast.error('Failed to assign job');
    }
  };

  const getAgentTierBadge = (qualityScore) => {
    if (qualityScore >= 9.0) return { label: 'Gold', color: '#FFD700' };
    if (qualityScore >= 8.0) return { label: 'Silver', color: '#C0C0C0' };
    return { label: 'Bronze', color: '#CD7F32' };
  };

  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }

  const chartData = [
    { name: 'Total Users', value: stats?.total_users || 0 },
    { name: 'Total Checks', value: stats?.total_checks || 0 },
    { name: 'Pending', value: stats?.pending_checks || 0 },
    { name: 'Completed', value: stats?.completed_checks || 0 }
  ];

  return (
    <div className="dashboard" data-testid="admin-dashboard">
      {/* Header */}
      <header className="dashboard-header">
        <div className="dashboard-logo">
          <img src="/hakicheck-logo.png" alt="HakiCheck Admin" className="header-logo" />
        </div>
        <div className="dashboard-user">
          <span className="user-name" data-testid="admin-name">{user?.full_name}</span>
          <button className="logout-btn" data-testid="logout-btn" onClick={handleLogout}>
            <LogOut size={18} style={{ marginRight: '0.5rem', display: 'inline' }} />
            Logout
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="dashboard-content">
        <div className="dashboard-welcome">
          <h1 className="welcome-title">Admin Dashboard</h1>
          <p className="welcome-subtitle">Monitor system performance and manage field agents</p>
        </div>

        {/* Tabs for different sections */}
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="agents">Field Agents</TabsTrigger>
            <TabsTrigger value="jobs">Agent Jobs</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            {/* Stats Grid */}
            <div className="stats-grid">
              <div className="stat-card primary">
                <Users size={32} style={{ marginBottom: '1rem', color: '#5a738f' }} />
                <div className="stat-label">Total Users</div>
                <div className="stat-value" data-testid="total-users">{stats?.total_users || 0}</div>
              </div>
              <div className="stat-card info">
                <FileCheck size={32} style={{ marginBottom: '1rem', color: '#0dcaf0' }} />
                <div className="stat-label">Total Checks</div>
                <div className="stat-value" data-testid="total-checks-admin">{stats?.total_checks || 0}</div>
              </div>
              <div className="stat-card warning">
                <TrendingUp size={32} style={{ marginBottom: '1rem', color: '#ffc107' }} />
                <div className="stat-label">Pending Checks</div>
                <div className="stat-value" data-testid="pending-checks-admin">{stats?.pending_checks || 0}</div>
              </div>
              <div className="stat-card success">
                <DollarSign size={32} style={{ marginBottom: '1rem', color: '#6a88a5' }} />
                <div className="stat-label">Total Revenue</div>
                <div className="stat-value" data-testid="total-revenue">${stats?.total_revenue || 0}</div>
              </div>
            </div>

            {/* Chart */}
            <div className="card">
              <h2 className="card-title">System Overview</h2>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="value" fill="#5a738f" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Recent Activity */}
            <div className="card">
              <h2 className="card-title">Recent Background Checks</h2>
              {stats?.recent_checks?.length === 0 ? (
                <p className="text-gray-500">No recent checks</p>
              ) : (
                <div className="recent-checks-list">
                  {stats?.recent_checks?.map((check, index) => (
                    <div key={check.id || index} className="recent-check-item" data-testid={`recent-check-${index}`}>
                      <div>
                        <h4>{check.subject_name}</h4>
                        <p className="text-sm text-gray-600">ID: {check.subject_id_number}</p>
                      </div>
                      <div className="text-right">
                        <span className={`status-badge ${check.status}`}>{check.status}</span>
                        <p className="text-sm text-gray-600">{check.payment_status}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Field Agents Tab */}
          <TabsContent value="agents">
            <div className="card">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="card-title mb-1">Field Agents</h2>
                  <p className="text-gray-600 text-sm">Manage field verification agents</p>
                </div>
                <Dialog open={showAddAgentDialog} onOpenChange={setShowAddAgentDialog}>
                  <DialogTrigger asChild>
                    <Button className="bg-[#5a738f] hover:bg-[#4a5f7f]">
                      <UserPlus size={18} className="mr-2" />
                      Add Agent
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>Register New Field Agent</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateAgent} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Full Name</label>
                          <input
                            type="text"
                            required
                            className="w-full p-2 border rounded-md"
                            value={agentForm.full_name}
                            onChange={(e) => setAgentForm({...agentForm, full_name: e.target.value})}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2">Phone</label>
                          <input
                            type="tel"
                            required
                            className="w-full p-2 border rounded-md"
                            value={agentForm.phone}
                            onChange={(e) => setAgentForm({...agentForm, phone: e.target.value})}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Email</label>
                        <input
                          type="email"
                          required
                          className="w-full p-2 border rounded-md"
                          value={agentForm.email}
                          onChange={(e) => setAgentForm({...agentForm, email: e.target.value})}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Regions Covered</label>
                        <div className="flex gap-2 mb-2">
                          <input
                            type="text"
                            placeholder="Enter region (e.g., Lagos, Abuja)"
                            className="flex-1 p-2 border rounded-md"
                            value={regionInput}
                            onChange={(e) => setRegionInput(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddRegion())}
                          />
                          <Button type="button" onClick={handleAddRegion} className="bg-[#5a738f]">
                            Add
                          </Button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {agentForm.regions_covered.map((region) => (
                            <span
                              key={region}
                              className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                            >
                              {region}
                              <button
                                type="button"
                                onClick={() => handleRemoveRegion(region)}
                                className="ml-2 text-blue-600 hover:text-blue-800"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="flex justify-end gap-2 pt-4">
                        <Button type="button" variant="outline" onClick={() => setShowAddAgentDialog(false)}>
                          Cancel
                        </Button>
                        <Button type="submit" className="bg-[#5a738f] hover:bg-[#4a5f7f]">
                          Register Agent
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              {agents.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Users size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No field agents registered yet</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {agents.map((agent) => {
                    const tier = getAgentTierBadge(agent.quality_score || 0);
                    return (
                      <div key={agent.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-lg">{agent.full_name}</h3>
                              <span
                                className="px-2 py-1 rounded text-xs font-bold"
                                style={{ backgroundColor: tier.color, color: '#fff' }}
                              >
                                {tier.label}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600">Code: {agent.agent_code}</p>
                          </div>
                          <div className="flex items-center gap-1 text-yellow-500">
                            <Star size={16} fill="currentColor" />
                            <span className="font-semibold">{agent.quality_score?.toFixed(1) || '0.0'}</span>
                          </div>
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center gap-2 text-gray-600">
                            <FileCheck size={14} />
                            <span>Jobs: {agent.total_jobs_completed || 0} completed</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-600">
                            <MapPin size={14} />
                            <span>{agent.regions_covered?.join(', ') || 'No regions'}</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-600">
                            <Award size={14} />
                            <span>Status: {agent.status || 'active'}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Agent Jobs Tab */}
          <TabsContent value="jobs">
            <div className="card">
              <h2 className="card-title mb-6">Field Verification Jobs</h2>
              {agentJobs.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Briefcase size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No field verification jobs available</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {agentJobs.map((job) => (
                    <div key={job.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-semibold">{job.job_type || 'Address Verification'}</h3>
                            <span className={`status-badge ${job.status}`}>{job.status}</span>
                          </div>
                          <div className="space-y-1 text-sm text-gray-600">
                            <p><strong>Check ID:</strong> {job.check_id}</p>
                            <p><strong>Address:</strong> {job.address || 'N/A'}</p>
                            {job.agent_id && <p><strong>Assigned Agent:</strong> {job.agent_id}</p>}
                            {job.agent_notes && <p><strong>Notes:</strong> {job.agent_notes}</p>}
                          </div>
                        </div>
                        {job.status === 'pending' && (
                          <div className="ml-4">
                            <select
                              className="p-2 border rounded-md text-sm"
                              onChange={(e) => e.target.value && handleAssignJob(job.id, e.target.value)}
                              defaultValue=""
                            >
                              <option value="">Assign to agent...</option>
                              {agents.filter(a => a.status === 'active').map(agent => (
                                <option key={agent.id} value={agent.id}>
                                  {agent.full_name} ({agent.agent_code})
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      <style>{`
        .loading-screen {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.2rem;
          color: #6c757d;
        }

        .recent-checks-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .recent-check-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem;
          background: #f8f9fa;
          border-radius: 10px;
          border: 1px solid #e9ecef;
        }

        .recent-check-item h4 {
          margin: 0 0 0.25rem 0;
          font-size: 1rem;
          font-weight: 600;
          color: #212529;
        }

        .status-badge {
          display: inline-block;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 600;
          text-transform: capitalize;
        }

        .status-badge.pending {
          background: #fff3cd;
          color: #856404;
        }

        .status-badge.in_progress {
          background: #cfe2ff;
          color: #084298;
        }

        .status-badge.completed {
          background: #d1e7dd;
          color: #0f5132;
        }

        .status-badge.failed {
          background: #f8d7da;
          color: #842029;
        }
      `}</style>
    </div>
  );
}

export default AdminDashboard;
